import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { X, ArrowsClockwise as RefreshCw, PiggyBank } from "@phosphor-icons/react";
import { PlusCircle, CheckCircle, ArrowSquareOut, Check as CheckIcon, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CaddyErrorCard } from "../components/CaddyErrorCard";
import confetti from "canvas-confetti";
import { SheetSyncBadge } from "../components/SheetSyncBadge";
import { CancelSurveyCard } from "../components/CancelSurveyCard";
import { CaddySidebar } from "../components/CaddySidebar";
import { CaddyBottomNav } from "../components/CaddyBottomNav";
import { SetupBasicsCard } from "../components/SetupBasicsCard";
import { RecapShare } from "../components/RecapCard";
import { FinisherCertificate } from "../components/FinisherCertificate";
import { PayoutChecklist } from "../components/PayoutChecklist";
import { PendingPayments } from "../components/PendingPayments";
import { CaddyTour } from "../components/CaddyTour";
import { ActualsCard } from "../components/ActualsCard";
import { WeekPlanCard } from "../components/WeekPlanCard";
import { AllocationSummaryCard } from "../components/AllocationSummaryCard";
import { RenewalNudgeCard } from "../components/RenewalNudgeCard";
import { SprintBanner } from "../components/game/SprintBanner";
import { SeasonSetupGate } from "../components/game/SeasonSetupGate";
import { LockedCard } from "../components/game/LockedCard";
import { InstallAppCard } from "../components/InstallAppCard";
import { RelayPrimer, RaceContextLine } from "../components/RelayPrimer";
import { StartHereCard } from "../components/StartHereCard";
import { OnboardingResearchCard } from "../components/OnboardingResearchCard";
import { AboutYouCard } from "../components/AboutYouCard";
import { RelayLoader } from "../components/RelayLoader";
import { homeSection } from '../lib/marketingHome';
import { mask, usePrivacy } from '../lib/privacy';

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR = {
  bg: "var(--pr-bg)",
  ink: "var(--pr-ink)",
  ink2: "var(--pr-ink2)",
  muted: "var(--pr-muted)",
  line: "var(--pr-line)",
  card: "var(--pr-card)",
  gold: "var(--pr-gold)",
  red: "var(--pr-red)",
  green: "var(--pr-green)",
  teal: "var(--pr-teal)",
};

const fmt = (n) => (n == null ? "n/a" : mask(`$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`));
const formatShortDate = (iso) => {
  if (!iso) return "";
  const [, m, d] = iso.split("-").map(Number);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[m - 1]} ${d}`;
};
const dayLabel = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
const fullDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric" });

const fundingIdea = (total) => {
  if (total < 50) return "That's your coffees covered for the week, earned above your target.";
  if (total < 150) return "That's a nice dinner out, fully funded by green weeks.";
  if (total < 400) return "That could cover a utility bill, or a guilt-free day off.";
  if (total < 1000) return "That's a car payment or a weekend getaway, funded by extra earnings.";
  if (total < 2500) return "That could fund a month of groceries, or a serious boost to your tax cushion.";
  return "That's real cushion, think a week off, a tax buffer, or a big dent in a goal.";
};

const PhoneMock = ({ variant }) => (
  <div
    className="w-44 h-[360px] rounded-[2.4rem] border-[6px] p-2.5 shadow-xl flex flex-col overflow-hidden"
    style={{ borderColor: COLOR.ink, background: "var(--pr-bg)" }}
    aria-hidden="true"
  >
    <div className="w-14 h-3.5 rounded-full mx-auto mb-2 flex-shrink-0" style={{ background: "var(--pr-ink-surface)" }} />
    <p className="text-[7px] font-bold tracking-widest uppercase text-center mb-1.5" style={{ color: COLOR.muted }}>Payday Relay</p>
    {variant === "red" ? (
      <>
        <div className="rounded-xl p-2.5 text-center mb-1.5" style={{ background: "var(--pr-card)" }}>
          <p className="text-[6px] font-bold uppercase tracking-widest" style={{ color: COLOR.muted }}>Left this week</p>
          <p className="text-lg font-bold" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.red }}>$120.00</p>
          <div className="h-1.5 rounded-full mt-1.5 overflow-hidden" style={{ background: COLOR.line }}>
            <div className="h-1.5 rounded-full w-3/4" style={{ background: "var(--pr-gold)" }} />
          </div>
        </div>
        <div className="rounded-xl p-2 mb-1.5" style={{ background: "var(--pr-tint-gold)" }}>
          <p className="text-[6px] font-bold uppercase" style={{ color: "var(--pr-accent-gold-text)" }}>Coach's tip</p>
          <p className="text-[7px] leading-snug" style={{ color: COLOR.ink }}>You are close. One steady push clears this Relay.</p>
        </div>
        <div className="rounded-xl p-2 mb-1.5" style={{ background: "var(--pr-card)" }}>
          <div className="flex justify-between text-[7px] mb-0.5" style={{ color: COLOR.ink2 }}><span>Mon · today</span><b style={{ color: COLOR.green }}>$220.00</b></div>
          <div className="flex justify-between text-[7px] mb-0.5" style={{ color: COLOR.ink2 }}><span>Tue</span><b style={{ color: COLOR.green }}>$160.00</b></div>
          <div className="flex justify-between text-[7px]" style={{ color: COLOR.muted }}><span>Wed</span><span>, </span></div>
        </div>
        <div className="flex gap-1">
          <div className="flex-1 rounded-full px-2 py-1 text-[7px]" style={{ background: "var(--pr-card)", color: COLOR.muted }}>$ Log a win…</div>
          <div className="rounded-full px-2 py-1 text-[7px] font-bold" style={{ background: "var(--pr-gold)", color: "var(--pr-on-gold)" }}>Log</div>
        </div>
      </>
    ) : (
      <>
        <div className="rounded-xl p-2.5 text-center mb-1.5" style={{ background: "var(--pr-tint-green)" }}>
          <p className="text-[6px] font-bold uppercase tracking-widest" style={{ color: COLOR.green }}>In the green</p>
          <p className="text-lg font-bold" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.green }}>+$85.00</p>
          <div className="h-1.5 rounded-full mt-1.5" style={{ background: "var(--pr-green-surface)" }} />
        </div>
        <p className="text-[7px] font-bold text-center mb-1.5" style={{ color: COLOR.ink }}>🔥 12 relays cleared this race</p>
        <div className="rounded-xl p-2 mb-1.5" style={{ background: "var(--pr-card)" }}>
          <p className="text-[6px] font-bold uppercase" style={{ color: COLOR.green }}>💰 Bonus bank</p>
          <p className="text-[9px] font-bold" style={{ color: COLOR.green }}>$450.00 this race</p>
        </div>
        <div className="rounded-xl p-1.5 text-center text-[7px] font-bold mb-1.5" style={{ background: "var(--pr-tint-green)", color: COLOR.green }}>✓ Synced with your sheet</div>
        <div className="rounded-xl p-1.5 text-center text-[7px] font-bold" style={{ background: "var(--pr-card)", color: COLOR.ink }}>📸 Share my recap card</div>
      </>
    )}
    <div className="mt-auto pt-2 flex-shrink-0">
      <div className="w-16 h-1 rounded-full mx-auto" style={{ background: "var(--pr-ink-surface)", opacity: 0.25 }} />
    </div>
  </div>
);

const Disclaimer = () => (
  <p className="text-[11px] text-center leading-relaxed px-4 pb-8" style={{ color: COLOR.muted }} data-testid="caddy-disclaimer">
    Payday Relay is an educational tool. Not financial, tax, or investment advice. For specifics, talk to a CPA.{" "}
    <Link to="/terms" className="underline">Terms</Link>
  </p>
);

// Small pill on the Daily screen. Pulses green during the "recalibration week"// (either the Pit Stop is OPEN now, or opens within the next 7 days).
// Otherwise renders a quiet indigo chip with a plain countdown.
const PitStopChip = ({ pitStop, resetDone = false }) => {
  if (!pitStop) return null;
  const isOpen = pitStop.is_open;
  const daysUntil = pitStop.next_window?.days_until;
  const isImminent = !isOpen && typeof daysUntil === "number" && daysUntil <= 7 && daysUntil >= 0;
  // Only surface near the quarter boundary: open now, or within two weeks of the
  // next quarterly tax day. Stays out of the way the rest of the quarter.
  const near = isOpen || (typeof daysUntil === "number" && daysUntil >= 0 && daysUntil <= 14);
  if (!near) return null;
  // Once the racer has recalibrated inside an open window, the pill goes quiet.
  const done = isOpen && resetDone;
  const pulse = !done && (isOpen || isImminent);

  const label = done
    ? `✓ ${pitStop.current_window.quarter} numbers set`
    : isOpen
      ? `🟢 Quarterly tax day open · ${pitStop.current_window.days_left} day${pitStop.current_window.days_left === 1 ? "" : "s"} left`
      : `🗓️ Next quarterly tax day in ${daysUntil} day${daysUntil === 1 ? "" : "s"} · ${formatShortDate(pitStop.next_window.opens_on)}`;

  const styles = pulse
    ? { background: "var(--pr-tint-green)", border: "1px solid var(--pr-green)", color: "var(--pr-accent-green-text)" }
    : { background: "var(--pr-tint-ink)", border: "1px solid var(--pr-card-line)", color: COLOR.ink };

  return (
    <>
      <style>{`
        @keyframes pit-stop-chip-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(76,175,80,0.55); }
          50%      { box-shadow: 0 0 0 8px rgba(76,175,80,0); }
        }
        .pit-stop-chip-pulse { animation: pit-stop-chip-pulse 1.8s ease-in-out infinite; }
      `}</style>
      <div
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold ${pulse ? "pit-stop-chip-pulse" : ""}`}
        style={styles}
        data-testid="caddy-pit-stop-chip"
        data-state={done ? "set" : isOpen ? "open" : "closed"}
        aria-live="polite"
      >
        {isOpen && !done ? (
          <Link to="/app/reset" className="underline-offset-2 hover:underline" data-testid="caddy-pit-stop-chip-label">{label} · Recalibrate →</Link>
        ) : (
          <span data-testid="caddy-pit-stop-chip-label">{label}</span>
        )}
      </div>
    </>
  );
};

// Everything that isn't "today's move" lives behind one door. Closed on every
// visit so the screen always opens on: your goal → log a win → your progress.
// Inline "share your week" prompt that appears only after a relay close-out.
// Reviewers get three answers: Yes (show the recap sharer), No thanks (skip
// until the next close-out), Don't ask again (persistently disabled).
const RelayShareAsk = ({ relayNumber, children }) => {
  const key = `caddy.recap.skip.r${relayNumber}`;
  const disabledKey = "caddy.recap.disabled";
  const [answer, setAnswer] = useState(() => {
    try {
      if (localStorage.getItem(disabledKey) === "1") return "disabled";
      if (localStorage.getItem(key) === "1") return "no";
    } catch { /* noop */ }
    return null;
  });
  if (answer === "disabled" || answer === "no") return null;
  if (answer === "yes") return <>{children}</>;
  return (
    <div className="rounded-2xl p-5 lg:p-6 pr-card" data-testid="caddy-recap-ask">
      <p className="text-[10px] font-bold uppercase tracking-widest mb-0.5" style={{ color: COLOR.muted }}>📸 Share this relay?</p>
      <p className="text-sm font-bold mb-1" style={{ color: "var(--pr-ink)" }} data-testid="caddy-recap-ask-title">
        Nice work closing Relay #{relayNumber}. Want to turn it into a shareable win card?
      </p>
      <p className="text-xs leading-relaxed mb-3" style={{ color: "var(--pr-ink2)" }}>
        Send it to someone rooting for your race, or keep it for yourself.
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setAnswer("yes")}
          className="px-4 py-2 rounded-full font-bold text-xs pr-btn-primary"
          data-testid="caddy-recap-ask-yes"
        >
          Yes, show me →
        </button>
        <button
          type="button"
          onClick={() => { try { localStorage.setItem(key, "1"); } catch { /* noop */ } setAnswer("no"); }}
          className="px-4 py-2 rounded-full font-bold text-xs pr-btn-ghost"
          data-testid="caddy-recap-ask-no"
        >
          No thanks
        </button>
        <button
          type="button"
          onClick={() => { try { localStorage.setItem(disabledKey, "1"); } catch { /* noop */ } setAnswer("disabled"); }}
          className="px-3 py-2 text-[11px] underline"
          style={{ color: "var(--pr-muted)" }}
          data-testid="caddy-recap-ask-never"
        >
          Don't ask me again
        </button>
      </div>
    </div>
  );
};

// New racers see one path, not twenty modules: finish the map → set the start
// date → log the first win. The rest of the dashboard stays closed until then.
const OnboardingChecklist = ({ steps, onSkip }) => {
  const done = steps.filter((st) => st.done).length;
  return (
    <div
      className="rounded-2xl p-6 lg:p-8 pr-card"
      data-testid="caddy-onboarding-checklist"
    >
      <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: COLOR.muted }}>
        Getting set up · {done} of {steps.length} done
      </p>
      <h2 className="text-xl mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
        Calculate → sync your sheet → start logging.
      </h2>
      <ol className="space-y-2.5">
        {steps.map((st, i) => {
          const active = !st.done && steps.slice(0, i).every((p) => p.done);
          return (
            <li
              key={st.id}
              className="flex items-start gap-3 rounded-xl p-4"
              style={{
                background: st.done ? "var(--pr-tint-green)" : active ? "var(--pr-bg-alt)" : "transparent",
                border: `1px solid ${st.done ? "var(--pr-card-line)" : active ? "var(--pr-card-line)" : "transparent"}`,
                opacity: st.done || active ? 1 : 0.5,
              }}
              data-testid={`caddy-onboarding-step-${st.id}`}
            >
              <span
                className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-bold mt-0.5"
                style={{ background: st.done ? "var(--pr-green-surface)" : "var(--pr-ink-surface)", color: "#fff" }}
              >
                {st.done ? <CheckIcon size={12} weight="bold" /> : i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold" style={{ color: COLOR.ink, textDecoration: st.done ? "line-through" : "none" }}>
                  {st.title}
                </p>
                <p className="text-xs leading-relaxed mt-0.5" style={{ color: COLOR.ink2 }}>{st.body}</p>
                {!st.done && active && st.cta && (
                  st.cta.href ? (
                    <a
                      href={st.cta.href}
                      className="inline-block mt-2.5 px-5 py-2.5 rounded-full text-xs font-bold pr-btn-primary"
                      data-testid={`caddy-onboarding-cta-${st.id}`}
                    >
                      {st.cta.label}
                    </a>
                  ) : st.cta.to ? (
                    <Link
                      to={st.cta.to}
                      className="inline-block mt-2.5 px-5 py-2.5 rounded-full text-xs font-bold pr-btn-primary"
                      data-testid={`caddy-onboarding-cta-${st.id}`}
                    >
                      {st.cta.label}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={st.cta.onClick}
                      className="inline-block mt-2.5 px-5 py-2.5 rounded-full text-xs font-bold pr-btn-primary"
                      data-testid={`caddy-onboarding-cta-${st.id}`}
                    >
                      {st.cta.label}
                    </button>
                  )
                )}
                {!st.done && active && st.skip && (
                  <button
                    type="button"
                    onClick={st.skip.onClick}
                    className="block mt-2 text-[11px] underline"
                    style={{ color: COLOR.muted }}
                    data-testid={`caddy-onboarding-skip-${st.id}`}
                  >
                    {st.skip.label}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      <button
        type="button"
        onClick={onSkip}
        className="mt-3 text-[11px] underline"
        style={{ color: COLOR.muted }}
        data-testid="caddy-onboarding-skip"
      >
        Skip setup, show me everything
      </button>
    </div>
  );
};

// Pending Payments UI stays OFF until the app and the Google Sheet are validated
// together. The gate is now PER-ACCOUNT: summary.pending_enabled is true only when
// the member's email is in the server-side rollout allowlist (the SAME allowlist
// that enables the sheet writers), so the UI can never appear for a non-migrated
// account. No global flag exposes it.

export default function CaddyPage() {
  usePrivacy();  // re-render when Creator Privacy Mode flips
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState("loading"); // loading | signed_out | not_member | member
  const [summary, setSummary] = useState(null);
  const [amount, setAmount] = useState("");
  const [isPending, setIsPending] = useState(false);
  const [expectedDate, setExpectedDate] = useState("");
  const [pendingRefresh, setPendingRefresh] = useState(0);
  const [logDate, setLogDate] = useState("");
  const [showDate, setShowDate] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [logging, setLogging] = useState(false);
  const [cancelSurveyDone, setCancelSurveyDone] = useState(false);
  const [dayEarned, setDayEarned] = useState(0);
  const monthCacheRef = useRef({});
  const [milestone, setMilestone] = useState(null);
  const [showTour, setShowTour] = useState(false);
  const [refWelcomeHidden, setRefWelcomeHidden] = useState(false);
  const [caddyOnlyHidden, setCaddyOnlyHidden] = useState(() => {
    try { return localStorage.getItem("caddy.onlymode.dismissed") === "1"; } catch { return false; }
  });
  const [onboardingSkipped, setOnboardingSkipped] = useState(() => {
    try { return localStorage.getItem("caddy.onboarding.skipped") === "1"; } catch { return false; }
  });
  const [syncSkipped, setSyncSkipped] = useState(() => {
    try { return localStorage.getItem("caddy.onboarding.sync.skipped") === "1"; } catch { return false; }
  });
  const [pitStop, setPitStop] = useState(null);
  const [justClosedRelay, setJustClosedRelay] = useState(null); // set when ActualsCard.onSaved fires, powers the "Share this relay?" ask
  const [showFresh, setShowFresh] = useState(false);
  const [seasonSetupOpen, setSeasonSetupOpen] = useState(false);
  const [seasonRecalibrated, setSeasonRecalibrated] = useState(false);
  const [freshAck, setFreshAck] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [nameSaving, setNameSaving] = useState(false);
  const [nameSkipped, setNameSkipped] = useState(() => {
    try { return localStorage.getItem("caddy.name.skipped") === "1"; } catch { return false; }
  });

  const fireMilestone = () => {
    confetti({ particleCount: 200, spread: 100, origin: { y: 0.4 }, colors: ["#F5CB5C", "#3E8E4E", "#2A1E5C", "#DE3C4B"] });
    setTimeout(() => confetti({ particleCount: 120, spread: 120, origin: { y: 0.6 }, colors: ["#F5CB5C", "#FFF3C6"] }), 400);
  };

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/caddy/summary`, { credentials: "include" });
      if (res.status === 401) { setView("signed_out"); return; }
      const data = await res.json().catch(() => null);
      if (res.status === 403) {
        const d = data?.detail;
        if (d && typeof d === "object" && d.code === "trial_ended") { setView("sprint_complete"); return; }
        setView("not_member");
        return;
      }
      if (!res.ok || !data) { toast.error(data?.detail || "Couldn't load Payday Relay"); return; }
      setSummary(data);
      setLogDate(data.today);
      setView("member");
      if (data.milestone) { setMilestone(data.milestone); fireMilestone(); }
      try { if (localStorage.getItem("caddy.tour.done") !== "1") setShowTour(true); } catch { /* noop */ }
      if (searchParams.get("tour") === "1") { setShowTour(true); setSearchParams({}, { replace: true }); }
      if (searchParams.get("fresh") === "1") { setFreshAck(false); setShowFresh(true); setSearchParams({}, { replace: true }); }
      if (searchParams.get("season-setup") === "1") { setSeasonSetupOpen(true); setSearchParams({}, { replace: true }); }
      if (searchParams.get("recalibrated") === "1") { setSeasonSetupOpen(true); setSeasonRecalibrated(true); setSearchParams({}, { replace: true }); }
    } catch {
      if (summary) toast.error("Couldn't refresh, check your connection and try again");
      else setView("error");
    }
  };

  // Google Sheets reconnect lands back here (not on the public site), confirm it.
  useEffect(() => {
    const sheetParam = searchParams.get("sheet");
    if (!sheetParam) return;
    if (sheetParam === "reconnected") {
      toast.success("Sheet reconnected ✓. Your wins are syncing again");
    } else {
      toast.error("Google didn't finish connecting, give it another go from the sync banner");
    }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pull-to-refresh (touch devices)
  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const pullRef = useRef({ startY: 0, active: false });

  useEffect(() => {
    const prev = document.body.style.overscrollBehaviorY;
    document.body.style.overscrollBehaviorY = "contain";
    return () => { document.body.style.overscrollBehaviorY = prev; };
  }, []);

  const onPullStart = (e) => {
    if (window.scrollY > 5 || refreshing) return;
    pullRef.current = { startY: e.touches[0].clientY, active: true };
  };
  const onPullMove = (e) => {
    if (!pullRef.current.active || refreshing) return;
    const dy = e.touches[0].clientY - pullRef.current.startY;
    if (dy > 0 && window.scrollY <= 0) setPullY(Math.min(dy * 0.45, 80));
    else if (pullY !== 0) setPullY(0);
  };
  const onPullEnd = async () => {
    if (!pullRef.current.active) return;
    pullRef.current.active = false;
    if (pullY > 55 && !refreshing) {
      setRefreshing(true);
      setPullY(48);
      try {
        await load();
        toast.success("Week re-synced ✓");
      } finally {
        setRefreshing(false);
        setPullY(0);
      }
    } else {
      setPullY(0);
    }
  };

  useEffect(() => {
    load();
    // Load Pit Stop status (universal calendar. No auth needed)
    fetch(`${API}/api/caddy/pit-stop-status`).then(r => r.ok ? r.json() : null).then(setPitStop).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pit Stop reminder toast, fires ONCE per window the day the chip first
  // turns green (window open OR opens within ≤7 days). Keyed on opens_on in
  // localStorage so it never repeats within the same recalibration window.
  useEffect(() => {
    if (!pitStop || !summary || summary?.entitlements?.mode === "sprint") return;
    const gateStage = summary?.season_setup?.stage;
    if (gateStage === "required" || gateStage === "build_pending") return; // gate owns the screen
    const isOpen = pitStop.is_open;
    if (isOpen && summary?.quarter?.reset_done) return; // numbers already set. Nothing to nag about
    // The Pit Stop screen opens itself once per quarter, for racers already past onboarding.
    if (isOpen && summary?.weekly_target && summary?.has_logged_ever) {
      const openedKey = `caddy.pitstop.opened.${pitStop.current_window?.quarter}.${pitStop.current_window?.opens_on}`;
      let opened = false;
      try { opened = localStorage.getItem(openedKey) === "1"; } catch { /* noop */ }
      if (!opened) {
        try { localStorage.setItem(openedKey, "1"); } catch { /* noop */ }
        navigate("/app/reset?auto=1");
        return;
      }
    }
    const daysUntil = pitStop.next_window?.days_until;
    const isImminent = !isOpen && typeof daysUntil === "number" && daysUntil <= 7 && daysUntil >= 0;
    if (!isOpen && !isImminent) return;
    const opensOn = isOpen ? pitStop.current_window?.opens_on : pitStop.next_window?.opens_on;
    if (!opensOn) return;
    const key = `caddy.pitstop.reminder.${opensOn}`;
    try { if (localStorage.getItem(key) === "1") return; } catch { /* storage unavailable */ }
    const quarter = isOpen ? pitStop.current_window.quarter : pitStop.next_window.quarter;
    const message = isOpen
      ? `🟢 Your ${quarter} quarterly tax day window is open. You have until ${formatShortDate(pitStop.current_window.closes_on)} to recalibrate.`
      : `🗓️ ${quarter} quarterly tax day is in ${daysUntil} day${daysUntil === 1 ? "" : "s"}. Time to plan your recalibration.`;
    // Small delay so it lands after the initial render + tour flow
    const timer = setTimeout(() => {
      toast(message, {
        duration: 10000,
        action: { label: "Reset now", onClick: () => navigate("/app/reset") },
        id: `pit-stop-reminder-${opensOn}`,
      });
      try { localStorage.setItem(key, "1"); } catch { /* storage unavailable */ }
    }, 1200);
    return () => clearTimeout(timer);
  }, [pitStop, navigate, summary]);

  // Existing earnings for the selected log date, powers the inline edit panel
  const selectedDate = showDate && logDate ? logDate : summary?.today;
  useEffect(() => {
    if (view !== "member" || !selectedDate) { setDayEarned(0); return; }
    const month = selectedDate.slice(0, 7);
    const cached = monthCacheRef.current[month];
    if (cached) { setDayEarned(cached[selectedDate] || 0); return; }
    let alive = true;
    fetch(`${API}/api/caddy/calendar?month=${month}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!json) return;
        const map = {};
        (json.days || []).forEach((d) => { if (d.earned) map[d.date] = d.earned; });
        monthCacheRef.current[month] = map;
        if (alive) setDayEarned(map[selectedDate] || 0);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [view, selectedDate]);


  const submitLog = async (amt, dateArg, mode, opts = {}) => {
    setLogging(true);
    try {
      const body = { amount: amt, date: dateArg, mode };
      if (opts.status) body.status = opts.status;
      if (opts.expectedDate) body.expected_date = opts.expectedDate;
      const res = await fetch(`${API}/api/caddy/log`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        const d = data.detail;
        const msg = typeof d === "string" ? d : d?.message || "Couldn't log that, try again";
        if (res.status === 409 && d && typeof d === "object" && d.code === "day_already_logged") {
          toast(msg, {
            id: "log-add-or-replace",
            duration: 15000,
            action: { label: `Add it → ${fmt(d.existing + amt)}`, onClick: () => submitLog(amt, dateArg, "add") },
            cancel: { label: `Replace → ${fmt(amt)}`, onClick: () => submitLog(amt, dateArg, "replace") },
          });
        } else if (d && typeof d === "object" && d.renew_url) {
          toast.error(msg, { action: { label: "Start a new Relay", onClick: () => { window.location.href = d.renew_url; } } });
        } else {
          toast.error(msg);
        }
        return;
      }
      const firstWin = !(summary?.has_logged_ever);
      setSummary(data);
      monthCacheRef.current = {};
      setDayEarned(0);
      setAmount("");
      setShowDate(false);
      setLogDate(data.today);
      setIsPending(false);
      setExpectedDate("");
      setPendingRefresh((n) => n + 1);
      if (data.milestone) {
        setMilestone(data.milestone);
        fireMilestone();
      } else if (data.celebrate) {
        confetti({ particleCount: 120, spread: 75, origin: { y: 0.6 }, colors: ["#F5CB5C", "#3E8E4E", "#2A1E5C"] });
      }
      if (opts.status === "pending") {
        toast.success(`${fmt(data.logged.amount)} marked earned, awaiting payment ⏳`, {
          description: "Counts toward your target now. Record the payment here when it lands.",
        });
      } else if (data.logged && !data.logged.synced_to_sheet && data.sheet_url) {
        toast.message(mode === "replace" ? "Day updated in Payday Relay" : "Logged in Payday Relay", { description: data.sheet_error || "We'll keep it here for you." });
      } else if (mode === "replace") {
        toast.success(amt === 0 ? "Earnings cleared for that day ✓" : `Day set to ${fmt(data.logged.amount)} ✓`);
      } else if (data.remaining > 0) {
        toast.success(`Win logged, ${fmt(data.logged.amount)}. You're ${fmt(data.remaining)} from clearing this relay.`);
      } else {
        toast.success(`Win logged, ${fmt(data.logged.amount)} 🏁`);
      }
      if (firstWin && data.has_logged_ever) {
        fireMilestone();
        setTimeout(() => toast.success("You're set up and racing 🏁 System tools are unlocked at the bottom of your screen."), 900);
      }
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setLogging(false);
    }
  };

  const logWin = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0 || logging) return;
    if (isPending) {
      await submitLog(amt, showDate ? logDate : undefined, undefined,
                      { status: "pending", expectedDate: expectedDate || undefined });
    } else {
      await submitLog(amt, showDate ? logDate : undefined, dayEarned > 0 ? "add" : undefined);
    }
  };

  const saveName = async (e) => {
    e.preventDefault();
    const n = nameInput.trim();
    if (!n || nameSaving) return;
    setNameSaving(true);
    try {
      const res = await fetch(`${API}/api/caddy/preferred-name`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: n }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "Couldn't save that, try again"); return; }
      setSummary((prev) => ({ ...prev, preferred_name: data.preferred_name }));
      toast.success(`Great to have you, ${data.preferred_name} 🏁`);
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setNameSaving(false);
    }
  };

  const skipName = () => {
    setNameSkipped(true);
    try { localStorage.setItem("caddy.name.skipped", "1"); } catch { /* noop */ }
  };

  const skipOnboarding = () => {
    setOnboardingSkipped(true);
    try { localStorage.setItem("caddy.onboarding.skipped", "1"); } catch { /* noop */ }
  };

  const dismissRefWelcome = async () => {
    setRefWelcomeHidden(true);
    try {
      await fetch(`${API}/api/caddy/referral-welcome-dismiss`, { method: "POST", credentials: "include" });
    } catch { /* stays hidden this session */ }
  };

  const signOut = async () => {
    try { await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" }); } catch { /* noop */ }
    setView("signed_out");
    setSummary(null);
  };

  if (view === "error") return <CaddyErrorCard />;

  if (view === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--pr-bg)" }} data-testid="caddy-loading">
        <div className="text-center">
          <RelayLoader label="Warming up Payday Relay…" />
        </div>
      </div>
    );
  }

  if (view === "signed_out") {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: "var(--pr-bg)" }}>
        <main className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-5xl" data-testid="caddy-signed-out">
            <div className="flex items-center justify-center gap-10 lg:gap-16">
              <div className="hidden md:block -rotate-6 flex-shrink-0" data-testid="caddy-mock-left"><PhoneMock variant="red" /></div>
              <div className="max-w-md w-full text-center">
                <p className="text-xs font-bold tracking-[0.25em] uppercase mb-3" style={{ color: COLOR.muted }}>Your race companion</p>
                <h1 className="text-4xl sm:text-5xl mb-4" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
                  Payday Relay
                </h1>
                <p className="text-base sm:text-lg leading-relaxed mb-8" style={{ color: COLOR.ink2 }}>
                  Your pocket pit crew. Log wins in seconds, watch your week clear, and every dollar writes straight
                  into <b style={{ color: COLOR.ink }}>your Payday Relay race on Google Sheets</b>.
                </p>
                <Link
                  to="/login?next=/app"
                  className="inline-block w-full max-w-xs px-8 py-4 rounded-full font-bold text-lg transition-transform hover:scale-[1.02]"
                  style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
                  data-testid="caddy-signin-link"
                >
                  Sign in with email →
                </Link>
                <p className="text-sm mt-4" style={{ color: COLOR.muted }}>
                  No password, no app store, a secure sign-in link lands in your inbox.
                </p>
                <p className="text-sm mt-2" style={{ color: COLOR.muted }}>
                  It comes with every Payday Relay plan.{" "}
                  <Link to={homeSection('season-pass')} className="underline" style={{ color: COLOR.ink }} data-testid="caddy-learn-more">
                    See what's included →
                  </Link>
                </p>
              </div>
              <div className="hidden md:block rotate-6 flex-shrink-0" data-testid="caddy-mock-right"><PhoneMock variant="green" /></div>
            </div>
            <div className="md:hidden flex justify-center mt-10" data-testid="caddy-mock-mobile"><PhoneMock variant="green" /></div>
          </div>
        </main>
        <Disclaimer />
      </div>
    );
  }

  if (view === "sprint_complete") {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: "var(--pr-bg)" }}>
        <main className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="max-w-md w-full text-center" data-testid="sprint-complete-screen">
            <p className="text-xs font-bold tracking-[0.25em] uppercase mb-2" style={{ color: COLOR.muted }}>Free Sprint · 4 of 4</p>
            <h1 className="text-3xl sm:text-4xl mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
              Your 4-Relay Sprint is complete.
            </h1>
            <p className="text-sm leading-relaxed mb-6" style={{ color: COLOR.ink2 }}>
              Nice work. Your Sprint gave you the rhythm. The full 52-week Payday Relay race starts fresh with a new sheet, you'll keep your Sprint settings or adjust your first quarter before you begin.
            </p>
            <Link
              to="/start/season-pass"
              className="inline-block px-8 py-4 rounded-full font-bold text-lg transition-transform hover:scale-[1.02]"
              style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
              data-testid="sprint-complete-cta"
            >
              Start My Payday Relay Race →
            </Link>
            <p className="text-xs mt-4" style={{ color: COLOR.muted }}>Cancel anytime. Your sheet stays in your Google Drive either way.</p>
            <p className="text-xs mt-6">
              <button onClick={signOut} className="underline" style={{ color: COLOR.muted }} data-testid="sprint-complete-signout">
                Signed in with the wrong email? Sign out
              </button>
            </p>
          </div>
        </main>
        <Disclaimer />
      </div>
    );
  }

  if (view === "not_member") {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: "var(--pr-bg)" }}>
        <main className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="max-w-sm w-full text-center" data-testid="caddy-join-gate">
            <h1 className="text-3xl mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
              Ready to run the race?
            </h1>
            <p className="text-sm leading-relaxed mb-8" style={{ color: COLOR.ink2 }}>
              This email doesn't have an active race yet. Run four relays free, or start your full
              52-week season with a Season Pass.
            </p>
            <Link
              to="/start/free-sprint"
              className="inline-block px-8 py-3.5 rounded-full font-bold text-base transition-transform hover:scale-105"
              style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
              data-testid="caddy-join-cta"
            >
              Start Free Sprint →
            </Link>
            <p className="text-sm mt-4">
              <Link to={homeSection('season-pass')} className="underline" style={{ color: COLOR.ink }} data-testid="caddy-see-pricing">
                See Season Pass pricing →
              </Link>
            </p>
            <p className="text-xs mt-6">
              <button onClick={signOut} className="underline" style={{ color: COLOR.muted }} data-testid="caddy-gate-signout">
                Signed in with the wrong email? Sign out
              </button>
            </p>
          </div>
        </main>
        <Disclaimer />
      </div>
    );
  }

  const s = summary;
  const ent = s.entitlements;
  const isSprint = ent?.mode === "sprint";
  const hasTarget = s.weekly_target != null && s.weekly_target > 0;
  const totalRelays = s.streak?.total || (isSprint ? 4 : 52);
  const earnedEff = (s.earned || 0) + (s.covered || 0);
  const pct = hasTarget ? Math.min((earnedEff / s.weekly_target) * 100, 100) : 0;
  const statusColor = s.status === "green" ? COLOR.green : s.status === "cleared" ? COLOR.ink : COLOR.red;
  const showNamePrompt = !s.preferred_name && !nameSkipped;
  const seasonSetup = s.season_setup;
  const seasonGateVisible = seasonSetup && (
    seasonSetup.stage === "required" || seasonSetup.stage === "build_pending" ||
    (seasonSetupOpen && seasonSetup.stage === "eligible")
  );

  // ---- State-aware onboarding: map → start date → first win ----
  const hasEverLogged = !!s.has_logged_ever || (s.earned || 0) > 0 || (s.streak?.completed || 0) > 0;
  const onboardingSteps = [
    {
      id: "map",
      title: "Calculate your relay numbers",
      body: "Your weekly target comes from the Relay Calculator, it's what every screen in here measures against.",
      done: hasTarget || !!s.sheet_sync?.connected,
      cta: { to: "/app/calculator?onboarding=1", label: "Open Relay Calculator →" },
    },
    {
      id: "sync",
      title: "Sync your Google Sheet",
      body: "Connect Google once and your Payday Relay sheet gets built in your own Drive, pre-loaded with your numbers. Every win you log lands in it automatically.",
      // Racers already logging (app-only by choice) are past this step.
      done: !!s.sheet_sync?.connected || hasEverLogged || syncSkipped,
      cta: s.setup_link
        ? { href: `${s.setup_link}&from=app`, label: "Sync my sheet →" }
        : { to: "/app/calculator?onboarding=1", label: "Lock in my numbers first →" },
      skip: { label: "Not now, log in the app only", onClick: () => { setSyncSkipped(true); try { localStorage.setItem("caddy.onboarding.sync.skipped", "1"); } catch { /* noop */ } } },
    },
    {
      id: "start",
      title: "Set your Start Date",
      body: "Pick the day your race begins and your payday rhythm. This locks your 52 relays into a calendar.",
      done: !!s.calendar?.start_date,
      cta: {
        label: "Set my race calendar →",
        onClick: () => document.querySelector('[data-testid="setup-basics-card"]')?.scrollIntoView({ behavior: "smooth", block: "center" }),
      },
    },
    {
      id: "win",
      title: "Log your first win",
      body: "Any amount you've earned. That one entry starts your streak and switches the whole dashboard on.",
      done: hasEverLogged,
      cta: {
        label: "Log a win →",
        onClick: () => document.querySelector('[data-testid="caddy-amount-input"]')?.focus(),
      },
    },
  ];
  const onboarded = onboardingSkipped || onboardingSteps.every((st) => st.done);

  const betaChip = s.beta_tester && (
    <span
      className="inline-block px-1.5 py-[1px] rounded-full text-[7px] font-bold leading-none"
      style={{ background: "var(--pr-gold)", color: "var(--pr-on-gold)", letterSpacing: "1.2px" }}
      data-testid="caddy-beta-badge"
      title="Thanks for helping us build Payday Relay"
    >
      BETA
    </span>
  );

  // ---- Today's move: the daily log ----
  const logCard = (
    <div
      className="pt-5 mt-5"
      style={{ borderTop: `1px solid ${COLOR.line}` }}
      data-testid="caddy-log-card"
    >
      <button
        type="button"
        onClick={() => setLogOpen((v) => !v)}
        className="w-full flex items-center justify-between"
        aria-expanded={logOpen}
        data-testid="caddy-log-toggle"
      >
        <span className="text-base font-bold inline-flex items-center gap-2" style={{ color: COLOR.ink }}>
          <PlusCircle size={18} weight="regular" /> Log income
        </span>
        <CaretDown size={16} className="transition-transform" style={{ transform: logOpen ? "rotate(180deg)" : "none", color: COLOR.muted }} />
      </button>
      {logOpen && (
      <form onSubmit={logWin} className="mt-4" data-testid="caddy-log-form">
      <div className="max-w-md mx-auto lg:max-w-none">
        <div className="flex items-center justify-between gap-2 mb-2.5">
          <p className="text-sm font-bold" style={{ color: selectedDate === s.today ? COLOR.ink : COLOR.gold }} data-testid="caddy-log-date-label">
            {selectedDate === s.today ? "Today" : formatShortDate(selectedDate)}
          </p>
          {showDate ? (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <input
                type="date"
                value={logDate}
                max={s.today}
                onChange={(e) => setLogDate(e.target.value)}
                className="text-xs px-2 py-1 rounded border"
                style={{ borderColor: COLOR.line, color: COLOR.ink }}
                data-testid="caddy-date-input"
              />
              <button
                type="button"
                onClick={() => { setShowDate(false); setLogDate(s.today); }}
                className="text-[11px] underline whitespace-nowrap"
                style={{ color: COLOR.muted }}
                data-testid="caddy-reset-today"
              >
                today
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowDate(true)}
              className="text-[11px] underline whitespace-nowrap flex-shrink-0"
              style={{ color: COLOR.muted }}
              data-testid="caddy-different-day"
            >
              Different day?
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            {(amount !== "" || dayEarned <= 0) && (
              <span className="absolute left-4 top-1/2 -translate-y-1/2 font-bold" style={{ color: COLOR.muted }}>$</span>
            )}
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={dayEarned > 0 ? "" : "0.00"}
              disabled={logging}
              className={`w-full ${(amount !== "" || dayEarned <= 0) ? "pl-8" : "pl-4"} pr-3 py-3 rounded-full pr-input text-base font-semibold`}
              style={{ color: COLOR.ink }}
              data-testid="caddy-amount-input"
            />
          </div>
          <button
            type="submit"
            disabled={logging || !parseFloat(amount)}
            className="px-5 py-3 rounded-full font-bold text-sm inline-flex items-center gap-1.5 disabled:opacity-40 pr-btn-primary"
            data-testid="caddy-log-btn"
          >
            {dayEarned > 0 && !isPending
              ? <CheckCircle size={17} weight="regular" className="flex-shrink-0" />
              : <PlusCircle size={17} weight="regular" className="flex-shrink-0" />}
            {logging ? "Logging…" : isPending ? "Mark earned" : dayEarned > 0 ? "Add it" : "Log income"}
          </button>
        </div>
        {/* Received vs awaiting payment — pending money counts toward the target but never looks spendable */}
        {s.pending_enabled && (<>
        <div className="flex items-center gap-1.5 mt-2.5 p-0.5 rounded-full w-fit" style={{ background: "var(--pr-bg)", border: `1px solid ${COLOR.line}` }} data-testid="caddy-income-status-toggle">
          <button
            type="button"
            onClick={() => setIsPending(false)}
            aria-pressed={!isPending}
            className="px-2.5 py-1 rounded-full text-[11px] font-bold transition-colors"
            style={!isPending ? { background: COLOR.ink, color: "#fff" } : { color: COLOR.muted }}
            data-testid="caddy-status-received"
          >
            Payment received
          </button>
          <button
            type="button"
            onClick={() => setIsPending(true)}
            aria-pressed={isPending}
            className="px-2.5 py-1 rounded-full text-[11px] font-bold transition-colors inline-flex items-center gap-1"
            style={isPending ? { background: COLOR.gold, color: COLOR.ink } : { color: COLOR.muted }}
            data-testid="caddy-status-pending"
          >
            Earned, awaiting payment
          </button>
        </div>
        {isPending && (
          <div className="mt-2.5 rounded-xl p-3" style={{ background: "var(--pr-bg)", border: `1px solid ${COLOR.line}` }} data-testid="caddy-pending-help">
            <label className="block text-[11px] font-semibold mb-1" style={{ color: COLOR.ink }}>
              Expected payment date (optional)
            </label>
            <input
              type="date"
              value={expectedDate}
              min={s.today}
              onChange={(e) => setExpectedDate(e.target.value)}
              className="text-xs px-2 py-1 rounded border"
              style={{ borderColor: COLOR.line, color: COLOR.ink }}
              data-testid="caddy-expected-date"
            />
            <p className="text-[11px] mt-2 leading-snug" style={{ color: COLOR.ink2 }}>
              Counts toward this relay's target right away. We won't treat it as cash, or fund taxes,
              savings, debt, PTO or bonus, until you record the payment landing.
            </p>
          </div>
        )}
        </>
        )}
        {dayEarned > 0 && !isPending && (
          <div className="mt-4 rounded-xl p-4" style={{ background: "var(--pr-bg)", border: `1px solid ${COLOR.line}` }} data-testid="caddy-day-edit-panel">
            <p className="text-xs font-semibold mb-0.5" style={{ color: COLOR.ink }} data-testid="caddy-day-edit-existing">
              You logged {fmt(dayEarned)} {selectedDate === s.today ? "today" : `on ${formatShortDate(selectedDate)}`}.
            </p>
            <p className="text-[11px] mb-2" style={{ color: COLOR.muted }}>
              "Add it" stacks on top, or edit the day:
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={logging || !parseFloat(amount)}
                onClick={() => submitLog(parseFloat(amount), showDate ? logDate : undefined, "replace")}
                className="px-3.5 py-2 rounded-full text-xs font-bold disabled:opacity-40 pr-btn-ghost"
                data-testid="caddy-replace-day-btn"
              >
                Replace with {parseFloat(amount) > 0 ? fmt(parseFloat(amount)) : "new amount"}
              </button>
              <button
                type="button"
                disabled={logging}
                onClick={() => submitLog(0, showDate ? logDate : undefined, "replace")}
                className="px-3.5 py-2 rounded-full text-xs font-bold border disabled:opacity-40 transition-colors hover:bg-black/[0.02]"
                style={{ borderColor: "var(--pr-card-line)", color: COLOR.red, background: "var(--pr-card)" }}
                data-testid="caddy-clear-day-btn"
              >
                Clear day → $0
              </button>
            </div>
          </div>
        )}
      </div>
    </form>
      )}
    </div>
  );

  // ---- One progress bar: this relay ----
  const statusCard = (
    <div className="text-center" data-testid="caddy-status-card">
      {s.week_off ? (
        <div data-testid="caddy-pto-week">
          <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: COLOR.teal }}>PTO week 🌴</p>
          <p className="text-4xl font-bold mb-1" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.teal }} data-testid="caddy-big-number">
            Time off
          </p>
          <p className="text-xs leading-relaxed" style={{ color: COLOR.muted }}>
            This relay is planned time off. No target chasing, your streak is safe.
          </p>
          {(s.earned > 0 || s.covered > 0) && (
            <p className="text-[11px] mt-2" style={{ color: COLOR.ink2 }}>
              {s.earned > 0 ? `${fmt(s.earned)} still earned` : ""}
              {s.earned > 0 && s.covered > 0 ? " · " : ""}
              {s.covered > 0 ? `${fmt(s.covered)} covered from your PTO bank` : ""}
            </p>
          )}
        </div>
      ) : hasTarget ? (
        <>
          {s.status === "green" ? (
            <>
              <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: COLOR.green }}>In the green</p>
              <p className="text-4xl sm:text-[2.4rem] font-medium tracking-tight mb-1" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.green }} data-testid="caddy-big-number">
                +{fmt(s.surplus)}
              </p>
              <p className="text-xs" style={{ color: COLOR.muted }}>bonus past your {fmt(s.weekly_target)} target</p>
            </>
          ) : s.status === "cleared" ? (
            <>
              <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: COLOR.ink }}>Relay cleared</p>
              <p className="text-4xl sm:text-[2.4rem] font-medium tracking-tight mb-1" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }} data-testid="caddy-big-number">
                $0.00
              </p>
              <p className="text-xs" style={{ color: COLOR.muted }}>you hit your {fmt(s.weekly_target)} target 🏁</p>
            </>
          ) : (
            <>
              <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: COLOR.red }}>
                {s.status === "near" ? "Almost there" : "Left this week"}
              </p>
              <p className="text-4xl sm:text-[2.4rem] font-medium tracking-tight mb-1" style={{ fontFamily: "'Fraunces', Georgia, serif", color: statusColor }} data-testid="caddy-big-number">
                {fmt(s.remaining)}
              </p>
              <p className="text-xs" style={{ color: COLOR.muted }}>of your {fmt(s.weekly_target)} weekly target</p>
            </>
          )}
          <div className="mt-4 h-2.5 rounded-full overflow-hidden" style={{ background: COLOR.line }}>
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${pct}%`, background: s.status === "green" ? COLOR.green : s.status === "cleared" ? COLOR.ink : COLOR.gold }}
              data-testid="caddy-progress-bar"
            />
          </div>
        </>
      ) : (
        <div data-testid="caddy-no-target">
          <p className="text-sm font-bold mb-1" style={{ color: COLOR.ink }}>No weekly target yet</p>
          <p className="text-xs mb-4" style={{ color: COLOR.ink2 }}>
            Run the Relay Calculator and your target shows up here automatically.
          </p>
          <Link to="/app/calculator?onboarding=1" className="inline-block px-6 py-3 rounded-full text-sm font-bold pr-btn-primary">
            Open Relay Calculator →
          </Link>
        </div>
      )}
    </div>
  );

  // ---- What stays on Daily (post-declutter, iter 88) ----
  // Menu dropdown is gone. Money lanes moved up next to the log. Goal, badges,
  // notifications, tour replay, quarterly-reset link, PTO bank, and race reel
  // all live on other pages (More or Scoreboard) so Daily stays focused.
  const sheetBanks = s.banks?.source === "sheet" ? s.banks : null;
  const yearBonus = sheetBanks?.year?.bonus != null ? sheetBanks.year.bonus : s.surplus_bank?.total;
  const monthNet = sheetBanks?.month?.shortfall_gain;
  const hasBonus = yearBonus > 0 || (monthNet != null && monthNet !== 0);

  const homeTools = (
    <div className="space-y-5 lg:space-y-6" data-testid="caddy-home-tools">
      {s.pending_enabled && (
      <PendingPayments
        api={API}
        fmt={fmt}
        color={COLOR}
        today={s.today}
        refreshKey={pendingRefresh}
        onSummary={(data) => { setSummary(data); monthCacheRef.current = {}; }}
      />
      )}

      {/* App-only mode guide */}
      {!s.sheet_synced && !s.sheet_sync?.connected && !caddyOnlyHidden && (
        <div className="rounded-2xl p-5 lg:p-6 pr-card" data-testid="caddy-only-mode-guide">
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: COLOR.ink }}>💡 App-only mode, here's how to still get value</p>
            <button
              onClick={() => { setCaddyOnlyHidden(true); try { localStorage.setItem("caddy.onlymode.dismissed", "1"); } catch { /* noop */ } }}
              className="p-0.5 -mt-0.5"
              aria-label="Dismiss"
              data-testid="caddy-only-mode-dismiss"
            >
              <X className="w-4 h-4" style={{ color: COLOR.muted }} />
            </button>
          </div>
          <ul className="text-xs leading-relaxed space-y-1.5 mb-3" style={{ color: COLOR.ink2 }}>
            <li>• <b style={{ color: COLOR.ink }}>Log wins here on any day</b>, the Caddy remembers them and closes your week for you.</li>
            <li>• <b style={{ color: COLOR.ink }}>Copy into your sheet on your own time.</b> Weekly or monthly, whatever suits your rhythm.</li>
            <li>• <b style={{ color: COLOR.ink }}>Ready for auto-sync?</b> Connect Google Sheets any time and every win from today forward writes straight into your sheet.</li>
          </ul>
          {s.setup_link ? (
            <Link
              to={s.setup_link}
              className="inline-block text-xs font-bold underline"
              style={{ color: COLOR.ink }}
              data-testid="caddy-only-mode-connect-link"
            >
              Connect Google Sheets →
            </Link>
          ) : (
            <p className="text-xs" style={{ color: COLOR.muted }} data-testid="caddy-only-mode-setup-note">
              Check your Payday Relay setup email for the connect link, or write us and we'll resend it.
            </p>
          )}
        </div>
      )}

      {/* Bonus bank lives on the Scoreboard now, not on the daily screen. */}

      {/* Sprint racers see a locked bonus-bank teaser only */}
      {isSprint && (
        <LockedCard id="locked-bonus-bank" icon="💰" title="Bonus Bank"
          body="Every month you clear net-positive drops the surplus into a bank you can actually spend. Unlocks with the 52-week race." />
      )}

      {/* End-of-relay share ask, only appears after a fresh close-out.
          RecapShare is gated behind Yes; No hides until next close; Don't-ask
          silences it permanently. */}
      {justClosedRelay != null && hasTarget && (
        <RelayShareAsk relayNumber={justClosedRelay}>
          <RecapShare summary={s} />
        </RelayShareAsk>
      )}

      {/* Install-to-home-screen prompt, different from notifications, which
          live on More. Hide during the welcome tour. */}
      {!showTour && <InstallAppCard />}
    </div>
  );

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "var(--pr-bg)" }}
      data-testid="caddy-dashboard"
      onTouchStart={onPullStart}
      onTouchMove={onPullMove}
      onTouchEnd={onPullEnd}
    >
      {seasonGateVisible && (
        <SeasonSetupGate
          seasonSetup={seasonSetup}
          recalibrated={seasonRecalibrated}
          onClose={() => { setSeasonSetupOpen(false); setSeasonRecalibrated(false); }}
          onDone={() => { setSeasonSetupOpen(false); setSeasonRecalibrated(false); load(); }}
        />
      )}
      {/* Pull-to-refresh indicator (mobile) */}
      {(pullY > 0 || refreshing) && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-40 lg:hidden transition-transform"
          style={{ top: 6 + pullY * 0.5 }}
          data-testid="caddy-pull-refresh"
        >
          <div className="w-9 h-9 rounded-full border shadow-md flex items-center justify-center" style={{ background: "var(--pr-card)", borderColor: COLOR.line }}>
            <RefreshCw
              className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
              style={{ color: pullY > 55 || refreshing ? COLOR.teal : COLOR.muted, transform: refreshing ? undefined : `rotate(${pullY * 3.5}deg)` }}
            />
          </div>
        </div>
      )}
      {showTour && (
        <CaddyTour onDone={() => { setShowTour(false); try { localStorage.setItem("caddy.tour.done", "1"); } catch { /* noop */ } }} />
      )}

      {showFresh && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-5" style={{ background: "rgba(26,16,60,0.72)" }} data-testid="fresh-start-modal">
          <div className="w-full max-w-md rounded-3xl p-6" style={{ background: "var(--pr-card)" }}>
            <h2 className="text-2xl mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
              Start a fresh relay?
            </h2>
            <p className="text-sm leading-relaxed mb-3" style={{ color: COLOR.ink2 }}>
              Life happens, the race is built so you can start over any week of the year. Here's exactly what
              a fresh start means:
            </p>
            <ul className="text-xs leading-relaxed space-y-1.5 mb-4" style={{ color: COLOR.ink2 }}>
              <li>• <b style={{ color: COLOR.ink }}>A brand-new 52-relay sheet</b> is built in your Google Drive, starting at Relay 1.</li>
              <li>• <b style={{ color: COLOR.ink }}>Your current sheet is archived</b>. Nothing is deleted, it stays in your Drive for your records.</li>
              <li>• <b style={{ color: COLOR.ink }}>Your in-app race resets</b>, streak, milestones, and relay count start over.</li>
              <li data-testid="fresh-start-pricing-fyi">• 🎁 <b style={{ color: COLOR.ink }}>Free during the beta.</b> After beta, a new race is a fresh purchase, mid-race fresh starts are 50% off ($27) with your founding-member discount.</li>
            </ul>
            <label className="flex items-start gap-2.5 rounded-xl p-3 mb-4 cursor-pointer" style={{ background: "var(--pr-tint-red)" }}>
              <input
                type="checkbox"
                checked={freshAck}
                onChange={(e) => setFreshAck(e.target.checked)}
                className="mt-0.5 w-4 h-4"
                data-testid="fresh-start-ack-checkbox"
              />
              <span className="text-xs font-semibold" style={{ color: COLOR.ink }}>
                I understand this ends my current race and can't be undone.
              </span>
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setShowFresh(false)}
                className="flex-1 py-3 rounded-full font-bold text-sm pr-btn-ghost"
                data-testid="fresh-start-cancel"
              >
                Keep racing
              </button>
              <button
                onClick={() => navigate("/app/renew?fresh=1")}
                disabled={!freshAck}
                className="flex-1 py-3 rounded-full font-bold text-sm disabled:opacity-40"
                style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
                data-testid="fresh-start-confirm"
              >
                Start fresh →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile header, compact */}
      <header className="px-5 pt-4 pb-1 lg:hidden">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-1.5 min-w-0">
            {s.beta_tester && (
              <span
                className="inline-block px-1.5 py-0.5 rounded-full text-[9px]"
                style={{ background: "var(--pr-red-surface)", color: "#FFFFFF", letterSpacing: "1.5px" }}
                data-testid="caddy-beta-badge-mobile"
                title="Thanks for helping us build Payday Relay 🏁"
              >
                🧪 BETA TESTER
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {s.sheet_url && (
              <a href={s.sheet_url} target="_blank" rel="noreferrer" className="p-2 rounded-full hover:bg-black/5" aria-label="Open my sheet" data-testid="caddy-header-sheet-link">
                <ArrowSquareOut size={16} weight="regular" style={{ color: COLOR.muted }} />
              </a>
            )}
          </div>
        </div>
      </header>

      <div className="flex-1 w-full max-w-5xl mx-auto lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-10 lg:px-8 lg:pt-8">
        {/* Sidebar, desktop Command Center nav */}
        <CaddySidebar active="daily" sheetUrl={s.sheet_url} onSignOut={signOut} onFreshStart={isSprint ? undefined : () => { setFreshAck(false); setShowFresh(true); }} betaChip={betaChip} sprint={isSprint} />

        {/* Single focused column, today first, everything else behind one door */}
        <main className="px-5 pb-32 lg:px-0 lg:pb-10">
          <div className="max-w-md mx-auto lg:max-w-xl space-y-5 lg:space-y-7 pt-2 lg:pt-0">

            {/* Welcome headline + the goal */}
            <div className="pb-1 px-5 lg:px-6" data-testid="caddy-welcome-headline">
              <h1 className="text-2xl lg:text-3xl leading-snug" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
                Welcome back{s.preferred_name ? `, ${s.preferred_name}` : ""}.
              </h1>
              <p className="text-[13px] sm:text-base font-bold mt-2 leading-snug" style={{ color: COLOR.ink }} data-testid="caddy-relay-label">
                {s.relay_number ? `Relay ${s.relay_number} · ` : ""}
                <span className="whitespace-nowrap">{dayLabel(s.week_start)} – {dayLabel(s.week_end)}</span>
                {s.payout_date && (
                  <span className="block whitespace-nowrap mt-0.5" style={{ color: COLOR.red }} data-testid="caddy-payout-date">next payday {dayLabel(s.payout_date)}</span>
                )}
              </p>
              {pitStop && !isSprint && onboarded && (
                <div className="mt-2">
                  <PitStopChip pitStop={pitStop} resetDone={!!s.quarter?.reset_done} />
                </div>
              )}
            </div>

            {/* Free Sprint race status */}
            {isSprint && ent?.sprint && <SprintBanner sprint={ent.sprint} />}

            {/* Optional research choice, shown once during onboarding, separate from
                the required setup checklist and never blocking any core action. */}
            <OnboardingResearchCard />

            {/* One-time 'tell us about you' welcome card, shown once after signup, skippable. */}
            <AboutYouCard />

            {/* Recalibration window closing and nothing locked in yet, 7-day nudge / 2-day final warning */}
            {!isSprint && s.pit_stop_warning && (
              <Link
                to="/app/reset"
                className="block rounded-2xl p-4 border-2 active:scale-[0.99] transition-transform"
                style={s.pit_stop_warning.level <= 2
                  ? { background: "var(--pr-tint-red, #FDECEC)", borderColor: "var(--pr-red)" }
                  : { background: "var(--pr-tint-gold)", borderColor: "var(--pr-gold)" }}
                data-testid="caddy-recal-warning"
              >
                <p className="text-xs font-bold" style={{ color: "var(--pr-ink)" }} data-testid="caddy-recal-warning-title">
                  {s.pit_stop_warning.level <= 2 ? "⏰ Last call, " : "🔧 Heads up. "}
                  your {s.pit_stop_warning.quarter} quarterly tax day closes{" "}
                  {s.pit_stop_warning.days_left === 0 ? "today" : s.pit_stop_warning.days_left === 1 ? "tomorrow" : `in ${s.pit_stop_warning.days_left} days`}
                </p>
                <p className="text-[11px] mt-1 leading-relaxed" style={{ color: "var(--pr-ink2)" }}>
                  You haven't recalibrated yet. Lock in your {s.pit_stop_warning.quarter} numbers before{" "}
                  {formatShortDate(s.pit_stop_warning.closes_on)}, or confirm the same targets and keep racing. <b>Recalibrate →</b>
                </p>
              </Link>
            )}

            {/* Step-by-step setup for new racers */}
            {!onboarded && <OnboardingChecklist steps={onboardingSteps} onSkip={skipOnboarding} />}

            {/* What a relay is, taught once, re-openable from More */}
            {onboarded && (
              <RelayPrimer
                weekStart={s.week_start}
                relayNumber={s.relay_number}
                totalRelays={totalRelays}
                raceContext={s.race_context}
                forceOpen={searchParams.get("primer") === "1"}
              />
            )}

            {/* Post-setup guidance: what to do in your first relay */}
            {onboarded && (
              <StartHereCard
                summary={s}
                onFocusLog={() => document.querySelector('[data-testid="caddy-amount-input"]')?.focus()}
              />
            )}

            {/* Race calendar setup, app-only members without a start date */}
            {s.needs_setup_basics && (
              <SetupBasicsCard today={s.today} onSaved={(data) => { setSummary(data); setLogDate(data.today); }} />
            )}

            {/* Last relay close-out — a non-blocking check-in that stays easy to return to */}
            {s.actuals_prompt && (
              <ActualsCard
                prompt={s.actuals_prompt}
                required={s.closeout_required}
                onSaved={() => {
                  // Remember which relay was just closed so RelayShareAsk knows
                  // to prompt exactly once, keyed by relay number.
                  const rn = s.actuals_prompt?.relay_number ?? s.last_week?.relay_number ?? (s.relay_number ? s.relay_number - 1 : null);
                  if (rn != null) setJustClosedRelay(rn);
                  load();
                }}
              />
            )}

            {/* THIS WEEK, target, today's log, and the day plan in one module */}
            <div className="rounded-2xl p-6 lg:p-8 pr-card" data-testid="caddy-week-module">
              {statusCard}
              {logCard}
              <WeekPlanCard
                bare
                plan={s.week_plan}
                weeklyTarget={s.weekly_target}
                today={s.today}
                onLogDay={(d) => {
                  setLogOpen(true);
                  setShowDate(true);
                  setLogDate(d);
                  setTimeout(() => document.querySelector('[data-testid="caddy-amount-input"]')?.focus(), 60);
                }}
                onDeleteDay={(d) => submitLog(0, d, "replace")}
                onChanged={load}
              />
              <div className="text-right mt-2">
                <Link
                  to="/app/history"
                  className="text-xs font-semibold underline"
                  style={{ color: COLOR.ink2 }}
                  data-testid="caddy-history-link"
                >
                  Fix an earlier week →
                </Link>
              </div>
            </div>

            {/* Money lanes (This relay), moved out of the (now-removed) menu
                drawer to sit right beneath the log so racers see how each
                dollar splits into tax / debt / savings / PTO / take-home. */}
            {onboarded && <AllocationSummaryCard alloc={s.allocation_summary} />}

            {/* Payout day checklist */}
            {s.payout_today && s.payout_checklist?.length > 0 && (
              <PayoutChecklist items={s.payout_checklist} dateKey={s.today} />
            )}

            {/* One-time preferred name prompt */}
            {showNamePrompt && onboarded && (
              <form
                onSubmit={saveName}
                className="rounded-2xl p-5 lg:p-6 pr-card"
                data-testid="caddy-name-prompt"
              >
                <p className="text-sm font-bold mb-1" style={{ color: COLOR.ink }}>What should we call you?</p>
                <p className="text-xs mb-3" style={{ color: COLOR.ink2 }}>
                  First name or nickname, it's how your dashboard greets you. One-time thing.
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    maxLength={40}
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    placeholder="e.g. Dove"
                    className="flex-1 px-4 py-2.5 rounded-full pr-input text-sm font-semibold"
                    style={{ color: COLOR.ink }}
                    data-testid="caddy-name-input"
                  />
                  <button
                    type="submit"
                    disabled={nameSaving || !nameInput.trim()}
                    className="px-5 py-2.5 rounded-full font-bold text-sm disabled:opacity-40 pr-btn-primary"
                    data-testid="caddy-name-save"
                  >
                    {nameSaving ? "Saving…" : "Save"}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={skipName}
                  className="mt-2 text-[11px] underline"
                  style={{ color: COLOR.muted }}
                  data-testid="caddy-name-skip"
                >
                  Maybe later
                </button>
              </form>
            )}

            {/* Sync status + the link to the sheet, the last thing on the page */}
            <RenewalNudgeCard renewal={isSprint ? null : s.renewal} />

            {!isSprint && s.quarter.prompt && !pitStop && (
              <Link
                to="/app/reset"
                className="block rounded-2xl p-4 text-center font-bold text-xs pr-card-dark active:scale-[0.99] transition-transform"
                style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
                data-testid="caddy-quarterly-banner"
              >
                🛠️ New quarter, time for your Quarterly Reset →
              </Link>
            )}

            {/* Race complete, start a new race */}
            {s.race_complete && (
              <Link
                to="/app/renew"
                className="block rounded-2xl p-5 lg:p-6 pr-card transition-transform hover:scale-[1.005]"
                data-testid="caddy-race-complete-banner"
              >
                <div className="flex items-start gap-3">
                  <span className="text-2xl">🏁</span>
                  <div className="flex-1">
                    <p className="font-bold text-base" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
                      Race complete, start a new relay race
                    </p>
                    <p className="text-xs leading-relaxed mt-0.5" style={{ color: COLOR.ink2 }}>
                      You finished all 52 relays. Your finished sheet stays archived in your Drive, a fresh 52-week race is one tap away.
                    </p>
                    <p className="text-xs font-bold mt-1.5 underline" style={{ color: COLOR.ink }}>Start my next race →</p>
                  </div>
                </div>
              </Link>
            )}

            {/* Friend welcome, joined via referral */}
            {s.referral_welcome && !refWelcomeHidden && (
              <div className="rounded-2xl p-5 lg:p-6 pr-card flex items-start gap-3" data-testid="caddy-referral-welcome">
                <span className="text-2xl">🎁</span>
                <div className="flex-1">
                  <p className="font-bold text-sm" style={{ color: COLOR.ink }}>Welcome to the race!</p>
                  <p className="text-xs leading-relaxed mt-0.5" style={{ color: COLOR.ink2 }}>
                    You joined through <b style={{ color: COLOR.ink }}>{s.referral_welcome.referrer}</b>'s invite, 
                    their next month is on us because of you. That's how racers roll.
                  </p>
                </div>
                <button onClick={dismissRefWelcome} className="p-1" aria-label="Dismiss" data-testid="referral-welcome-dismiss">
                  <X className="w-4 h-4" style={{ color: COLOR.muted }} />
                </button>
              </div>
            )}

            {/* Milestone celebration */}
            {milestone && (
              <div className="rounded-2xl p-5 lg:p-6 pr-card flex items-start gap-3" data-testid="caddy-milestone-banner">
                <span className="text-2xl">🏆</span>
                <div className="flex-1">
                  <p className="font-bold text-base" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }} data-testid="caddy-milestone-title">
                    {milestone.title}
                  </p>
                  <p className="text-xs leading-relaxed mt-0.5" style={{ color: COLOR.ink2 }}>{milestone.message}</p>
                </div>
                <button onClick={() => setMilestone(null)} className="p-1" aria-label="Dismiss" data-testid="caddy-milestone-dismiss">
                  <X className="w-4 h-4" style={{ color: COLOR.muted }} />
                </button>
              </div>
            )}

            {/* Finish line, all 52 relays done */}
            {s.race_complete && <FinisherCertificate />}

            {/* Sprint complete, keep the race alive */}
            {isSprint && (s.trial?.show_upgrade || ent?.sprint?.days_left === 0) && (
              <div className="rounded-2xl p-6 lg:p-8 pr-card-dark" style={{ background: "var(--pr-ink-surface)" }} data-testid="trial-upgrade-banner">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-2" style={{ color: "var(--pr-gold)" }}>
                  🏁 Your 4-Relay Sprint is complete
                </p>
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: "#FFFFFF" }}>
                  Start your Payday Relay race.
                </h3>
                <p className="text-sm leading-relaxed mb-4" style={{ color: "rgba(255,255,255,0.85)" }}>
                  Your Sprint gave you the rhythm. The full 52-week race starts fresh with a new sheet, 
                  you'll keep your Sprint settings or adjust your first quarter. The Bonus Bank, PTO Bank,
                  and Quarterly Tax Days all open with your Season Pass.
                </p>
                <a
                  href="/start/season-pass"
                  className="inline-block px-6 py-3 rounded-full font-bold text-sm pr-btn-primary"
                  data-testid="trial-upgrade-cta"
                >
                  Start My Payday Relay Race →
                </a>
              </div>
            )}

            {/* One gentle question after a cancellation */}
            {s.cancel_survey_due && !cancelSurveyDone && (
              <CancelSurveyCard onDone={() => setCancelSurveyDone(true)} />
            )}

            {/* Everything else, flat, focused, one screen. Menu drawer is gone. */}
            {onboarded && <div className="pt-2">{homeTools}</div>}

            {/* Sheet sync + the link to open the sheet, last thing on the page */}
            <SheetSyncBadge sync={s.sheet_sync} sheetUrl={s.sheet_url} sheetError={s.sheet_error} />

            <div className="pt-2"><Disclaimer /></div>
          </div>
        </main>
      </div>
      <CaddyBottomNav active="daily" sprint={isSprint} />
    </div>
  );
}
